import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TargetModeSelect } from "@/components/ClassStudentSelect";
import { colorOf } from "@/lib/subjects";
import { ListTree, BookOpen, Plus, X, Trash2, Save } from "lucide-react";

const STATUS = {
  baslanmadi: { label: "Başlanmadı", chip: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-300" },
  calisiliyor: { label: "Çalışılıyor", chip: "bg-amber-100 text-amber-700 border-amber-300", dot: "bg-amber-500" },
  iyi: { label: "İyi", chip: "bg-sky-100 text-sky-700 border-sky-300", dot: "bg-sky-500" },
  bitti: { label: "Bitti", chip: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "bg-emerald-500" },
};
const ORDER = ["baslanmadi", "calisiliyor", "iyi", "bitti"];
const key = (s, t) => `${s}|||${t}`;

export default function KonuTakibi() {
  const { user } = useAuth();
  const isEditor = user?.role === "admin" || user?.role === "koc";
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [myGrade, setMyGrade] = useState(null);
  const [mode, setMode] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 1);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [subjects, setSubjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [progress, setProgress] = useState([]);
  const [newTopic, setNewTopic] = useState({});
  const [resources, setResources] = useState({});   // { subject_name: [{name,soru,dogru,yanlis,bos,net}] }

  useEffect(() => {
    if (isEditor) {
      api.get("/students").then(({ data }) => setStudents(data));
    } else {
      api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setMyGrade(data.grade ?? null); });
    }
  }, [isEditor]);

  const activeGrade = useMemo(
    () => (isEditor ? students.find((s) => s.id === studentId)?.grade ?? (mode === "class" ? clsGrade : null) : myGrade),
    [isEditor, students, studentId, mode, clsGrade, myGrade]
  );

  useEffect(() => {
    if (!activeGrade) { setSubjects([]); setUnits([]); return; }
    api.get("/subjects", { params: { grade: activeGrade } }).then(({ data }) => setSubjects(data));
    api.get("/units", { params: { grade: activeGrade } }).then(({ data }) => setUnits(data));
  }, [activeGrade]);

  const loadProgress = () => {
    if (mode === "class" || !studentId) { setProgress([]); setResources({}); return; }
    api.get("/topic-progress", { params: { student_id: studentId } }).then(({ data }) => setProgress(data));
    api.get("/konu-resources", { params: { student_id: studentId } }).then(({ data }) => {
      const m = {}; data.forEach((r) => { m[r.subject_name] = r.items || []; }); setResources(m);
    });
  };
  useEffect(() => { loadProgress(); }, [studentId, mode]);

  const progMap = useMemo(() => {
    const m = {};
    progress.forEach((p) => { m[key(p.subject_name, p.topic)] = p.status; });
    return m;
  }, [progress]);

  const topicsOf = (subjectName) => units.filter((u) => u.subject_name === subjectName).map((u) => u.name);

  const setStatus = async (subject, topic, status) => {
    try {
      if (mode === "class") {
        const { data } = await api.post("/topic-progress/assign-class", { grade: clsGrade, class_section: clsSection || null, subject_name: subject, topic, status });
        toast.success(`${data.count} öğrencinin "${topic}" konusu → ${STATUS[status].label}`);
      } else {
        if (!studentId) return toast.error("Önce öğrenci seçin");
        await api.post("/topic-progress", { student_id: studentId, subject_name: subject, topic, status });
        setProgress((prev) => {
          const others = prev.filter((p) => !(p.subject_name === subject && p.topic === topic));
          return [...others, { subject_name: subject, topic, status }];
        });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addTopic = async (subjectName) => {
    const name = (newTopic[subjectName] || "").trim();
    if (!name) return;
    try {
      await api.post("/units", { subject_name: subjectName, name, grade: activeGrade || null });
      const { data } = await api.get("/units", { params: { grade: activeGrade } });
      setUnits(data);
      setNewTopic((n) => ({ ...n, [subjectName]: "" }));
      toast.success("Konu eklendi ✓");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const blankRes = () => ({ name: "", soru: "", dogru: "", yanlis: "", bos: "" });
  const resOf = (subject) => resources[subject] || [];
  const setRes = (subject, next) => setResources((r) => ({ ...r, [subject]: next }));
  const addRes = (subject) => setRes(subject, [...resOf(subject), blankRes()]);
  const patchRes = (subject, i, field, v) => setRes(subject, resOf(subject).map((row, j) => (j === i ? { ...row, [field]: v } : row)));
  const removeRes = (subject, i) => setRes(subject, resOf(subject).filter((_, j) => j !== i));
  const saveRes = async (subject) => {
    if (!studentId) return toast.error("Önce öğrenci seçin");
    try {
      const { data } = await api.post("/konu-resources", { student_id: studentId, subject_name: subject, items: resOf(subject) });
      setRes(subject, data.items || []);
      toast.success(`${subject} kaynakları kaydedildi ✓`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-100 text-teal-600"><ListTree className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Konu Takibi</h1>
          <p className="text-slate-500">{isEditor ? "Öğrencinin her dersteki konularını renklendirerek takip edin: başlanmadı, çalışılıyor, iyi, bitti." : "Derslerindeki konuların durumu — renklerle takip."}</p>
        </div>
      </div>

      {/* Renk açıklaması */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <span className="text-sm font-bold text-slate-600">Durumlar:</span>
        {ORDER.map((s) => (
          <span key={s} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${STATUS[s].chip}`}>
            <span className={`h-2 w-2 rounded-full ${STATUS[s].dot}`} /> {STATUS[s].label}
          </span>
        ))}
      </div>

      {isEditor && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <TargetModeSelect students={students} mode={mode} setMode={setMode} studentId={studentId} setStudentId={(v) => setStudentId(v || null)} grade={clsGrade} setGrade={setClsGrade} section={clsSection} setSection={setClsSection} testid="kt-student-select" />
        </div>
      )}

      {!isEditor && !studentId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Bağlı öğrenci bulunamadı.</div>
      ) : isEditor && mode === "student" && !studentId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Konuları görmek için bir öğrenci seçin.</div>
      ) : subjects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Bu sınıf için tanımlı ders yok.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {subjects.map((subj) => {
            const topics = topicsOf(subj.name);
            const c = colorOf(subj.color);
            const done = topics.filter((t) => progMap[key(subj.name, t)] === "bitti").length;
            return (
              <div key={subj.id} data-testid={`kt-subject-${subj.name}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className={`flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 ${c.bg}`}>
                  <h3 className={`flex items-center gap-2 font-display text-lg font-bold ${c.text}`}><BookOpen className="h-5 w-5" /> {subj.name}</h3>
                  {mode === "student" && <span className="rounded-full bg-white/70 px-2.5 py-0.5 text-xs font-bold text-slate-600">{done}/{topics.length} bitti</span>}
                </div>
                <div className="space-y-2 p-4">
                  {topics.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Konu tanımlı değil{isAdmin ? " — aşağıdan ekleyin" : ""}.</p>}
                  {topics.map((t) => {
                    const st = mode === "class" ? null : (progMap[key(subj.name, t)] || "baslanmadi");
                    return (
                      <div key={t} data-testid={`kt-topic-${subj.name}-${t}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          {st && <span className={`h-2.5 w-2.5 rounded-full ${STATUS[st].dot}`} />}{t}
                        </span>
                        {isEditor ? (
                          <div className="flex flex-wrap gap-1">
                            {ORDER.map((s) => (
                              <button key={s} data-testid={`kt-set-${s}-${subj.name}-${t}`} onClick={() => setStatus(subj.name, t, s)}
                                className={`rounded-lg border px-2 py-1 text-[11px] font-bold transition active:scale-95 ${st === s ? STATUS[s].chip : "border-slate-200 bg-white text-slate-400 hover:bg-slate-100"}`}>
                                {STATUS[s].label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${STATUS[st].chip}`}>{STATUS[st].label}</span>
                        )}
                      </div>
                    );
                  })}
                  {isAdmin && (
                    <div className="flex items-center gap-2 pt-1">
                      <input data-testid={`kt-new-topic-${subj.name}`} value={newTopic[subj.name] || ""} onChange={(e) => setNewTopic((n) => ({ ...n, [subj.name]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addTopic(subj.name)} placeholder="Yeni konu ekle…" className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-teal-400" />
                      <button data-testid={`kt-add-topic-${subj.name}`} onClick={() => addTopic(subj.name)} className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-bold text-white transition hover:bg-teal-700 active:scale-95"><Plus className="h-4 w-4" /> Ekle</button>
                    </div>
                  )}

                  {mode === "student" && studentId && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Kaynaklar (Yayın · Soru · D/Y/B)</span>
                        {isEditor && <button data-testid={`kt-add-res-${subj.name}`} onClick={() => addRes(subj.name)} className="flex items-center gap-1 rounded-lg border border-teal-200 px-2 py-1 text-[11px] font-bold text-teal-700 hover:bg-teal-50"><Plus className="h-3.5 w-3.5" /> Kaynak</button>}
                      </div>
                      {resOf(subj.name).length === 0 ? (
                        <p className="py-2 text-center text-xs text-slate-400">Kaynak girilmedi.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {!isEditor && (
                            <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-1.5 px-1 text-[10px] font-bold text-slate-400">
                              <span>Yayın / Kitap</span><span className="w-10 text-center">Soru</span><span className="w-9 text-center">D</span><span className="w-9 text-center">Y</span><span className="w-9 text-center">B</span><span className="w-12 text-center">Net</span>
                            </div>
                          )}
                          {resOf(subj.name).map((row, i) => (
                            isEditor ? (
                              <div key={i} data-testid={`kt-res-row-${subj.name}-${i}`} className="flex flex-wrap items-center gap-1.5">
                                <input data-testid={`kt-res-name-${subj.name}-${i}`} value={row.name} onChange={(e) => patchRes(subj.name, i, "name", e.target.value)} placeholder="Yayın / Kitap adı" className="min-w-[120px] flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none focus:border-teal-400" />
                                <input type="number" min={0} value={row.soru} onChange={(e) => patchRes(subj.name, i, "soru", e.target.value)} placeholder="Soru" className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none focus:border-teal-400" />
                                <input type="number" min={0} value={row.dogru} onChange={(e) => patchRes(subj.name, i, "dogru", e.target.value)} placeholder="D" className="w-12 rounded-lg border border-emerald-200 px-2 py-1 text-xs outline-none focus:border-emerald-400" />
                                <input type="number" min={0} value={row.yanlis} onChange={(e) => patchRes(subj.name, i, "yanlis", e.target.value)} placeholder="Y" className="w-12 rounded-lg border border-rose-200 px-2 py-1 text-xs outline-none focus:border-rose-400" />
                                <input type="number" min={0} value={row.bos} onChange={(e) => patchRes(subj.name, i, "bos", e.target.value)} placeholder="B" className="w-12 rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none focus:border-slate-400" />
                                <button data-testid={`kt-res-del-${subj.name}-${i}`} onClick={() => removeRes(subj.name, i)} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            ) : (
                              <div key={i} data-testid={`kt-res-row-${subj.name}-${i}`} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-1.5 rounded-lg bg-slate-50/60 px-1 py-1 text-xs">
                                <span className="truncate font-semibold text-slate-700">{row.name || "—"}</span>
                                <span className="w-10 text-center text-slate-500">{row.soru || 0}</span>
                                <span className="w-9 text-center font-bold text-emerald-600">{row.dogru || 0}</span>
                                <span className="w-9 text-center font-bold text-rose-600">{row.yanlis || 0}</span>
                                <span className="w-9 text-center text-slate-500">{row.bos || 0}</span>
                                <span className="w-12 text-center font-extrabold text-teal-600">{row.net ?? 0}</span>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                      {isEditor && resOf(subj.name).length > 0 && (
                        <button data-testid={`kt-res-save-${subj.name}`} onClick={() => saveRes(subj.name)} className="mt-2 flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-teal-700 active:scale-95"><Save className="h-3.5 w-3.5" /> Kaynakları Kaydet</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
